import { useEffect, useState } from 'react';
import PageLayout from '../components/PageLayout';
import { useCompany } from '../context/CompanyContext';
import api from '../api';
import './Dashboard.css';

const FREQUENCY_LABELS = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annually: 'Annually',
  event_based: 'Event-based',
};

const EMPTY_CUSTOM_FORM = {
  category_id: '',
  form_name: '',
  description: '',
  frequency: 'annually',
  due_day: '',
  due_month: '',
  penalty_per_day: 0,
  reference_law: '',
  notes: '',
};

function ComplianceSetup() {
  const { selectedCompanyId, selectedCompany } = useCompany();

  const [categories, setCategories] = useState([]);
  const [templatesByCategory, setTemplatesByCategory] = useState({});
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [expandedIds, setExpandedIds] = useState(new Set());
  const [selectedTemplateIds, setSelectedTemplateIds] = useState({});
  const [addingCategoryId, setAddingCategoryId] = useState(null);

  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [customForm, setCustomForm] = useState(EMPTY_CUSTOM_FORM);
  const [savingCustom, setSavingCustom] = useState(false);
  const [customError, setCustomError] = useState(null);

  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  };

  const loadSubscriptions = () => {
    if (!selectedCompanyId) return Promise.resolve();
    return api.get(`/company/${selectedCompanyId}/subscriptions`).then((res) => setSubscriptions(res.data));
  };

  useEffect(() => {
    if (!selectedCompanyId) return;
    setLoading(true);
    api
      .get('/compliance-categories')
      .then((res) => {
        setCategories(res.data);
        const optional = res.data.filter((c) => !c.is_standard);
        return Promise.all(
          optional.map((c) => api.get(`/compliance-templates/${c.id}`).then((r) => [c.id, r.data]))
        );
      })
      .then((pairs) => {
        setTemplatesByCategory(Object.fromEntries(pairs));
        return loadSubscriptions();
      })
      .then(() => setError(null))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  const standardCategories = categories.filter((c) => c.is_standard);
  const optionalCategories = categories.filter((c) => !c.is_standard && c.name !== 'Custom');
  const subscribedTemplateIds = new Set(subscriptions.map((s) => s.template_id));
  const activeSubscriptions = subscriptions.filter((s) => s.is_active);

  const toggleExpanded = (categoryId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  };

  const toggleTemplateChecked = (categoryId, templateId) => {
    setSelectedTemplateIds((prev) => {
      const current = new Set(prev[categoryId] || []);
      if (current.has(templateId)) current.delete(templateId);
      else current.add(templateId);
      return { ...prev, [categoryId]: current };
    });
  };

  const addSelected = (category) => {
    const chosen = Array.from(selectedTemplateIds[category.id] || []).filter((id) => !subscribedTemplateIds.has(id));
    if (chosen.length === 0) return;
    setAddingCategoryId(category.id);
    Promise.all(chosen.map((templateId) => api.post(`/company/${selectedCompanyId}/subscribe`, { template_id: templateId })))
      .then(() => {
        showToast(`${chosen.length} ${chosen.length === 1 ? 'compliance' : 'compliances'} added to your calendar ✓`);
        setSelectedTemplateIds((prev) => ({ ...prev, [category.id]: new Set() }));
        return loadSubscriptions();
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setAddingCategoryId(null));
  };

  const openCustomModal = () => {
    setCustomForm(EMPTY_CUSTOM_FORM);
    setCustomError(null);
    setCustomModalOpen(true);
  };

  const saveCustom = (e) => {
    e.preventDefault();
    setCustomError(null);
    if (!customForm.category_id || !customForm.form_name.trim() || !customForm.frequency) {
      setCustomError('Compliance name, category and frequency are required.');
      return;
    }
    setSavingCustom(true);
    const payload = {
      category_id: Number(customForm.category_id),
      form_name: customForm.form_name.trim(),
      description: customForm.description || null,
      frequency: customForm.frequency,
      due_day: customForm.due_day ? Number(customForm.due_day) : null,
      due_month: customForm.frequency === 'annually' && customForm.due_month ? Number(customForm.due_month) : null,
      penalty_per_day: Number(customForm.penalty_per_day) || 0,
      reference_law: customForm.reference_law || null,
      notes: customForm.notes || null,
    };
    api
      .post('/compliance-templates', payload)
      .then((res) => {
        const template = res.data;
        return api.post(`/company/${selectedCompanyId}/subscribe`, { template_id: template.id, notes: customForm.notes || null });
      })
      .then(() => {
        showToast(`${payload.form_name} added to your calendar ✓`);
        setCustomModalOpen(false);
        return loadSubscriptions().then(() => {
          // Refresh the template list for whichever category it landed in, so
          // it shows up immediately if that card is later expanded.
          return api.get(`/compliance-templates/${payload.category_id}`).then((r) => {
            setTemplatesByCategory((prev) => ({ ...prev, [payload.category_id]: r.data }));
          });
        });
      })
      .catch((err) => setCustomError(err.response?.data?.detail || err.message))
      .finally(() => setSavingCustom(false));
  };

  const confirmRemove = () => {
    if (!removeTarget) return;
    setRemoving(true);
    api
      .delete(`/company/${selectedCompanyId}/unsubscribe/${removeTarget.template_id}`)
      .then(() => {
        showToast(`${removeTarget.template.form_name} removed`);
        setRemoveTarget(null);
        return loadSubscriptions();
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setRemoving(false));
  };

  const categoryName = (categoryId) => categories.find((c) => c.id === categoryId)?.name || '—';

  return (
    <PageLayout>
      <div className="page-title-row">
        <h1 className="page-title">Compliance Setup</h1>
        <p className="page-subtitle">Configure compliance requirements for {selectedCompany?.name || '...'}</p>
      </div>

      {toast && <div className="cal-toast">{toast}</div>}
      {error && <p className="dash-error">{error}</p>}

      <div className={loading ? 'dash-refreshing' : ''}>
        <div className="dash-card">
          <h2 className="dash-card-title">Standard Compliances (Always Active)</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 4 }}>
            {standardCategories.map((c) => (
              <span key={c.id} className="dash-pill dash-pill-green" style={{ fontSize: 14 }}>
                {c.icon} {c.name} ✓
              </span>
            ))}
          </div>
          <p className="dash-table-sub" style={{ marginTop: 10 }}>These apply to all companies</p>
        </div>

        <div className="dash-card">
          <h2 className="dash-card-title">Add Regulatory Compliances</h2>
          <p className="dash-table-sub" style={{ marginBottom: 16 }}>Select compliances applicable to your business</p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
            {optionalCategories.map((category) => {
              const templates = templatesByCategory[category.id] || [];
              const isExpanded = expandedIds.has(category.id);
              const activeCount = activeSubscriptions.filter((s) => s.template.category_id === category.id).length;
              const checked = selectedTemplateIds[category.id] || new Set();
              const newlyChecked = Array.from(checked).filter((id) => !subscribedTemplateIds.has(id)).length;

              return (
                <div key={category.id} className="dash-card" style={{ gridColumn: isExpanded ? '1 / -1' : undefined, margin: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: 22 }}>{category.icon}</div>
                      <h3 style={{ margin: '6px 0 2px' }}>{category.name}</h3>
                      <p className="dash-table-sub" style={{ margin: 0 }}>{category.description}</p>
                      <p className="dash-table-sub" style={{ margin: '6px 0 0' }}>
                        {templates.length} {templates.length === 1 ? 'filing' : 'filings'}
                        {activeCount > 0 && <span className="dash-pill dash-pill-green" style={{ marginLeft: 8 }}>{activeCount} active</span>}
                      </p>
                    </div>
                    <button
                      type="button"
                      className={`dash-pill ${isExpanded ? 'dash-pill-green' : 'dash-pill-blue'}`}
                      style={{ border: 'none', cursor: 'pointer', flexShrink: 0 }}
                      onClick={() => toggleExpanded(category.id)}
                    >
                      {isExpanded ? 'ON' : 'OFF'}
                    </button>
                  </div>
                  <button
                    type="button"
                    className="dash-btn-link"
                    style={{ background: 'none', border: 'none', color: '#c2410c', cursor: 'pointer', padding: 0, marginTop: 8, fontSize: 13 }}
                    onClick={() => toggleExpanded(category.id)}
                  >
                    {isExpanded ? 'Hide details' : 'View Details'}
                  </button>

                  {isExpanded && (
                    <div style={{ marginTop: 14, borderTop: '1px solid #e5e7eb', paddingTop: 14 }}>
                      {templates.length === 0 && <p className="dash-table-sub">No pre-built templates yet for this category — use Add Custom Compliance below.</p>}
                      {templates.map((t) => {
                        const alreadyAdded = subscribedTemplateIds.has(t.id);
                        return (
                          <label
                            key={t.id}
                            style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 0', opacity: alreadyAdded ? 0.6 : 1 }}
                          >
                            <input
                              type="checkbox"
                              checked={alreadyAdded || checked.has(t.id)}
                              disabled={alreadyAdded}
                              onChange={() => toggleTemplateChecked(category.id, t.id)}
                              style={{ marginTop: 3 }}
                            />
                            <span>
                              <strong>{t.form_name}</strong>{alreadyAdded && <span className="dash-pill dash-pill-green" style={{ marginLeft: 8, fontSize: 11 }}>Added ✓</span>}
                              <br />
                              <span className="dash-table-sub">
                                {FREQUENCY_LABELS[t.frequency] || t.frequency} — {t.due_description || 'No fixed due date'}
                                {t.reference_law && ` · ${t.reference_law}`}
                              </span>
                            </span>
                          </label>
                        );
                      })}
                      {templates.length > 0 && (
                        <div className="dash-btn-row" style={{ marginTop: 10 }}>
                          <button
                            type="button"
                            className="dash-btn dash-btn-orange"
                            disabled={newlyChecked === 0 || addingCategoryId === category.id}
                            onClick={() => addSelected(category)}
                          >
                            {addingCategoryId === category.id ? 'Adding...' : `Add Selected${newlyChecked ? ` (${newlyChecked})` : ''}`}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="dash-card">
          <h2 className="dash-card-title">Add Custom Compliance</h2>
          <p className="dash-table-sub" style={{ marginBottom: 16 }}>Add any other compliance not listed above</p>
          <button className="dash-btn dash-btn-orange" type="button" onClick={openCustomModal}>
            + Add Custom Compliance
          </button>
        </div>

        <div className="dash-card">
          <h2 className="dash-card-title">Your Active Compliances</h2>
          <table className="dash-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Frequency</th>
                <th>Due Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {activeSubscriptions.length === 0 && (
                <tr>
                  <td className="dash-empty" colSpan={6}>No custom compliances added yet.</td>
                </tr>
              )}
              {activeSubscriptions.map((s) => (
                <tr key={s.id}>
                  <td>{s.template.form_name}</td>
                  <td>{categoryName(s.template.category_id)}</td>
                  <td>{FREQUENCY_LABELS[s.template.frequency] || s.template.frequency}</td>
                  <td>{s.template.due_description || '—'}</td>
                  <td><span className="dash-pill dash-pill-green">Active</span></td>
                  <td>
                    <button className="dash-btn-red-outline" type="button" onClick={() => setRemoveTarget(s)}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {customModalOpen && (
        <div className="ncf-modal-overlay" onClick={() => setCustomModalOpen(false)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Add Custom Compliance</h3>
            <form onSubmit={saveCustom}>
              {customError && <p className="dash-error">{customError}</p>}
              <div className="field-group">
                <span className="field-label">Compliance Name *</span>
                <input
                  className="field-input"
                  required
                  value={customForm.form_name}
                  onChange={(e) => setCustomForm({ ...customForm, form_name: e.target.value })}
                />
              </div>
              <div className="field-group">
                <span className="field-label">Category *</span>
                <select
                  className="field-select"
                  required
                  value={customForm.category_id}
                  onChange={(e) => setCustomForm({ ...customForm, category_id: e.target.value })}
                >
                  <option value="">Select a category</option>
                  {categories.filter((c) => !c.is_standard).map((c) => (
                    <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
                  ))}
                </select>
              </div>
              <div className="field-group">
                <span className="field-label">Description</span>
                <input
                  className="field-input"
                  value={customForm.description}
                  onChange={(e) => setCustomForm({ ...customForm, description: e.target.value })}
                />
              </div>
              <div className="field-row">
                <div className="field-group">
                  <span className="field-label">Frequency *</span>
                  <select
                    className="field-select"
                    value={customForm.frequency}
                    onChange={(e) => setCustomForm({ ...customForm, frequency: e.target.value })}
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="annually">Annually</option>
                    <option value="event_based">Event-based</option>
                  </select>
                </div>
                {customForm.frequency !== 'event_based' && (
                  <div className="field-group">
                    <span className="field-label">
                      {customForm.frequency === 'monthly' ? 'Due Day (of following month)' : customForm.frequency === 'quarterly' ? 'Due Day (of month after quarter)' : 'Due Day'}
                    </span>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      className="field-input"
                      value={customForm.due_day}
                      onChange={(e) => setCustomForm({ ...customForm, due_day: e.target.value })}
                    />
                  </div>
                )}
              </div>
              {customForm.frequency === 'annually' && (
                <div className="field-group">
                  <span className="field-label">Due Month</span>
                  <select
                    className="field-select"
                    value={customForm.due_month}
                    onChange={(e) => setCustomForm({ ...customForm, due_month: e.target.value })}
                  >
                    <option value="">Select month</option>
                    {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((m, i) => (
                      <option key={m} value={i + 1}>{m}</option>
                    ))}
                  </select>
                </div>
              )}
              {customForm.frequency === 'event_based' && (
                <p className="dash-table-sub" style={{ marginTop: -8, marginBottom: 12 }}>
                  Event-based compliances don't get a dated calendar item — add one manually when the triggering event occurs.
                </p>
              )}
              <div className="field-group">
                <span className="field-label">Penalty per day ₹</span>
                <input
                  type="number"
                  className="field-input"
                  value={customForm.penalty_per_day}
                  onChange={(e) => setCustomForm({ ...customForm, penalty_per_day: e.target.value })}
                />
              </div>
              <div className="field-group">
                <span className="field-label">Reference Law / Regulation</span>
                <input
                  className="field-input"
                  value={customForm.reference_law}
                  onChange={(e) => setCustomForm({ ...customForm, reference_law: e.target.value })}
                />
              </div>
              <div className="field-group">
                <span className="field-label">Notes</span>
                <textarea
                  className="field-input"
                  rows={3}
                  value={customForm.notes}
                  onChange={(e) => setCustomForm({ ...customForm, notes: e.target.value })}
                />
              </div>
              <div className="ncf-modal-actions">
                <button type="button" className="dash-btn cal-btn-grey" onClick={() => setCustomModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="dash-btn dash-btn-orange" disabled={savingCustom}>
                  {savingCustom ? 'Saving...' : 'Save & Add to Calendar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {removeTarget && (
        <div className="ncf-modal-overlay" onClick={() => setRemoveTarget(null)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Remove Compliance</h3>
            <p style={{ fontSize: 14, color: '#374151' }}>
              Remove <strong>{removeTarget.template.form_name}</strong> from your compliance calendar?
              Any already-filed or overdue history stays untouched — only future pending items are removed.
            </p>
            <div className="ncf-modal-actions">
              <button className="dash-btn cal-btn-grey" type="button" onClick={() => setRemoveTarget(null)}>
                Cancel
              </button>
              <button className="dash-btn dash-btn-orange" style={{ background: '#dc2626' }} type="button" disabled={removing} onClick={confirmRemove}>
                {removing ? 'Removing...' : 'Yes, Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
}

export default ComplianceSetup;
